import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDateTime } from '../../lib/format';
import { useSequences } from '../automation/ActionEditor';
import { Badge, Button } from '../ui';

interface Enrollment { id: string; name: string; step: number; totalSteps: number; status: 'active' | 'done' | 'stopped'; stopReason: string | null; nextRunAt: string | null }

/** Follow-up sequences this lead is in, with start/stop. */
export function LeadSequences({ leadId }: { leadId: string }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['lead', leadId, 'sequences'], queryFn: () => api.get<Enrollment[]>(`/leads/${leadId}/sequences`) });
  const sequences = useSequences();
  const [pick, setPick] = useState('');
  const active = (sequences.data ?? []).filter((s) => s.isActive);
  if (!q.data || (!q.data.length && !active.length)) return null;
  const refresh = () => qc.invalidateQueries({ queryKey: ['lead', leadId] });
  const start = async () => {
    try { await api.post(`/leads/${leadId}/sequences`, { sequenceId: pick }); setPick(''); toast.success('Sequence started.'); await refresh(); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Couldn’t start it.'); }
  };
  const stop = async (id: string) => { await api.del(`/leads/${leadId}/sequences/${id}`); await refresh(); };
  return (
    <div className="mt-6 border-t border-slate-200 pt-4 dark:border-slate-800">
      <p className="mb-2 text-sm font-semibold">Follow-up sequences</p>
      <ul className="space-y-2">
        {q.data.map((e) => (
          <li key={e.id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{e.name}</span>
            <Badge tone={e.status === 'active' ? 'green' : 'gray'}>{e.status === 'active' ? `Step ${e.step + 1} of ${e.totalSteps}` : e.status === 'done' ? 'Finished' : 'Stopped'}</Badge>
            <span className="text-slate-500">{e.status === 'active' && e.nextRunAt ? `next ${formatDateTime(e.nextRunAt)}` : e.stopReason ?? ''}</span>
            {e.status === 'active' && can('leads.edit') && <button className="text-red-600 hover:underline" onClick={() => stop(e.id)}>Stop</button>}
          </li>
        ))}
      </ul>
      {can('leads.edit') && active.length > 0 && (
        <div className="mt-3 flex gap-2">
          <select aria-label="Sequence" value={pick} onChange={(e) => setPick(e.target.value)} className="h-9 flex-1 rounded-lg border border-slate-300 bg-white px-2 text-sm dark:border-slate-700 dark:bg-slate-900">
            <option value="">Start a sequence…</option>
            {active.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <Button size="sm" variant="secondary" disabled={!pick} onClick={start}>Start</Button>
        </div>
      )}
    </div>
  );
}
